import type { CurvaDocument } from "@/document/model/document";
import type { FrameNode } from "@/document/model/nodes";
import { exportSceneNodeThumbnailDataUrl } from "@/export/pngExporter";

/** Package path for the Finder / file-browser document preview. */
export const DOCUMENT_THUMBNAIL_PACKAGE_PATH = "previews/thumbnail.png";

const THUMBNAIL_PAGE_NAME = "thumbnail";
const DEFAULT_THUMBNAIL_BOX_SIZE = 512;

/**
 * Prefer a page named "Thumbnail" (case-insensitive); otherwise the first page.
 * On that page, use the first top-level frame.
 */
export function resolveDocumentThumbnailFrame(document: CurvaDocument): FrameNode | undefined {
  const thumbnailPage = document.pages.find(
    page => page.name.trim().toLowerCase() === THUMBNAIL_PAGE_NAME,
  );
  const page = thumbnailPage ?? document.pages[0];
  if (!page) {
    return undefined;
  }

  return page.children.find((node): node is FrameNode => node.type === "frame");
}

export async function renderDocumentThumbnailPngBytes(
  document: CurvaDocument,
  options: { boxSize?: number } = {},
): Promise<Uint8Array | undefined> {
  const frame = resolveDocumentThumbnailFrame(document);
  if (!frame) {
    return undefined;
  }

  const dataUrl = await exportSceneNodeThumbnailDataUrl(
    document,
    frame,
    options.boxSize ?? DEFAULT_THUMBNAIL_BOX_SIZE,
  );
  return dataUrlToPngBytes(dataUrl);
}

function dataUrlToPngBytes(dataUrl: string): Uint8Array {
  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex === -1) {
    return new Uint8Array();
  }

  const binary = atob(dataUrl.slice(commaIndex + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
